import axios from "axios";
import { BASE_URL } from "./BASE_URL";
import { clearAuthStorage } from "../utils/authStorage";

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  (config) => {
    // No need to manually attach tokens if we are using HTTP-only cookies
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (Unauthorized - expired token)
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axiosInstance(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        axios
          .post(`${BASE_URL}/refresh-token`, {}, { withCredentials: true })
          .then(({ data }) => {
            if (data.success) {
              processQueue(null, data.accessToken);
              resolve(axiosInstance(originalRequest));
            } else {
              processQueue(new Error("Refresh failed"), null);
              reject(new Error("Refresh failed"));
            }
          })
          .catch((err) => {
            processQueue(err, null);
            clearAuthStorage();
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);

// GET Request Deduplication Layer to address issue P19
const pendingRequests = new Map();

const getRequestKey = (config) => {
  return `${config.method || 'get'}_${config.url}_${JSON.stringify(config.params || {})}`;
};

const originalRequest = axiosInstance.request;
axiosInstance.request = function (config) {
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get') {
    const key = getRequestKey(config);
    if (pendingRequests.has(key)) {
      return pendingRequests.get(key);
    }
    const promise = originalRequest.call(this, config)
      .then(response => {
        pendingRequests.delete(key);
        return response;
      })
      .catch(error => {
        pendingRequests.delete(key);
        return Promise.reject(error);
      });
    pendingRequests.set(key, promise);
    return promise;
  }
  return originalRequest.call(this, config);
};

export default axiosInstance;
